import type { MyBusiness } from "@teamna/shared";
import { useCallback, useState, type ReactNode } from "react";
import {
  EmptyState,
  ErrorState,
  LockIcon,
  PageHeader,
  SkeletonList,
} from "../components/index.ts";

/**
 * The frame the owner pages share inside the desktop shell.
 *
 * Every one of them needs the same three answers before it can render a single row: is the business
 * list still loading, did it fail, and does this account run a business at all. Written once here so a
 * screen file is about its own job rather than about the same guard on every page.
 *
 * The business's name is not repeated in the header: the sidebar (or the top bar on a phone) already
 * says which business is open. Like every owner page, the body takes the full page column, so its
 * cards and message boxes line up with the header and the billing banner (decided 2026-10-10).
 *
 * Deliberately free of router hooks, so a screen can still be rendered on its own in a test.
 */
export function OwnerGate({
  title,
  business,
  isLoading,
  error,
  managedCount,
  back,
  children,
}: {
  title: string;
  business: MyBusiness | null;
  isLoading: boolean;
  error: unknown;
  managedCount: number;
  /** Set on a page pushed on top of a list, so it has a way back to the one it came from. */
  back?: { to: string; label: string };
  children: ReactNode;
}) {
  let body: ReactNode;

  if (isLoading && !business) {
    body = <SkeletonList />;
  } else if (error && !business) {
    body = <ErrorState error={error} />;
  } else if (managedCount === 0 || !business) {
    body = (
      <EmptyState
        icon={<LockIcon size={30} />}
        title="No business to manage"
        message="This account does not run a business. Ask us to set one up, or use the member side to see your own schedule."
      />
    );
  } else {
    body = children;
  }

  return (
    <div>
      <PageHeader title={title} {...(back ? { back } : {})} />
      {body}
    </div>
  );
}

export type OwnerNotice = { title: string; message: string };

/**
 * A notice waiting for the next owner screen to mount.
 *
 * One slot, module level, taken by the first `useOwnerNotice` that mounts after it is set. It
 * exists for the one action that ends by leaving its screen: an announcement is written on its own
 * screen and read on the channel, and the "sent to 42" receipt belongs on the second, not the
 * first.
 */
let pending: OwnerNotice | null = null;

export function handOffOwnerNotice(notice: OwnerNotice): void {
  pending = notice;
}

/**
 * The receipt for the last thing the owner did on this screen.
 *
 * Held per screen rather than globally: the sentence is about the row that is still on screen
 * behind it, so carrying it to another tab would be confusing rather than helpful. It stays until
 * dismissed, because the counts in it are worth reading twice.
 */
export function useOwnerNotice(): {
  value: OwnerNotice | null;
  show: (notice: OwnerNotice) => void;
  clear: () => void;
} {
  // A handed-off notice is collected once, when the screen mounts. Strict mode calls this twice
  // and keeps the first result, so the slot is empty by the time the discarded call runs.
  const [value, setValue] = useState<OwnerNotice | null>(() => {
    const taken = pending;
    pending = null;
    return taken;
  });
  const clear = useCallback(() => setValue(null), []);
  const show = useCallback((notice: OwnerNotice) => setValue(notice), []);
  return { value, show, clear };
}
